import { Check } from "lucide-react";

import { PageHero } from "@/components/hero/PageHero";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { AppEntryLink } from "@/components/navigation/AppEntryLink";
import { usePricingCopy } from "@/lib/pricingContext";
import { formatPriceParts } from "@/lib/pricing";

interface Tier {
  name: string;
  price: string;
  cents?: string;
  originalPrice?: string;
  /** Suffix after the price — "/ appeal" on paid, blank on free,
   *  "to start" on Custom. Empty string suppresses it. */
  priceSuffix?: string;
  blurb: string;
  features: string[];
  cta: string;
  /** App-surface route (`/session`) or external href (`mailto:…`).
   *  Both render a real `<a>`: external because it leaves the site,
   *  /session because entering the app must be a full document load
   *  (see `components/navigation/AppEntryLink`). */
  ctaTo: string;
  external?: boolean;
  highlight?: boolean;
  badge?: string;
  offer?: string;
}

const TIERS: Tier[] = [
  {
    name: "Free",
    price: "$0",
    blurb: "Read your denial. Understand it. Get a probability assessment.",
    features: [
      "OCR your denial letter",
      "Extract carrier, claim, denial reason, cited policies",
      "Probability of overturn on appeal",
      "Sample of relevant policy language",
      "No account required",
    ],
    cta: "Start free",
    ctaTo: "/session",
  },
  {
    name: "Full appeal",
    price: "",
    priceSuffix: "/ appeal",
    blurb: "Everything you need to mail a credible, evidence-cited appeal today.",
    features: [
      "Everything in Free",
      "Generate full appeal letter, section by section",
      "Cite your plan’s policy language with verified quotes",
      "Refine any section with diff / accept / revert",
      "Medical-record analysis (vitals, dates of service, lab values)",
      "Export to PDF and Word",
    ],
    cta: "Start free, pay when ready",
    ctaTo: "/session",
    highlight: true,
    badge: "The whole package",
  },
  {
    name: "Custom",
    price: "Let’s talk",
    blurb:
      "For patient advocates, clinics, and organizations handling appeals at volume — or anyone who needs more than a single letter.",
    features: [
      "Everything in Full appeal",
      "Bulk processing across multiple cases",
      "Custom payer / policy library",
      "API access for your own workflow",
      "Dedicated support and SLA",
      "Volume pricing",
    ],
    cta: "Contact us",
    ctaTo: "mailto:hello@caalm-ai.com?subject=Revetra%20%E2%80%94%20Custom%20plan",
    external: true,
    badge: "For teams",
  },
];

export function Pricing() {
  const { pricing } = usePricingCopy();

  const paidPrice = pricing.paid.has_offer
    ? pricing.paid.effective_price_cents
    : pricing.paid.original_price_cents;
  const paidPriceParts = formatPriceParts(paidPrice);
  const tiers = TIERS.map((tier) => {
    if (!tier.highlight) {
      return tier;
    }
    return {
      ...tier,
      price: paidPriceParts.price,
      cents: paidPriceParts.cents,
      originalPrice: undefined,
      offer: "Launch offer — limited time",
    };
  });
  const heroPaidCopy = `For a limited time, pay the ${paidPriceParts.label} launch offer only when you’re ready to draft the appeal.`;

  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      <Header />
      <main className="flex-1">
        <PageHero
          eyebrow="Pricing"
          title={
            <>
              One-time. Per appeal.{" "}
              <em className="italic text-ink-muted">No subscription.</em>
            </>
          }
          subtitle={`Read your denial for free. ${heroPaidCopy} For organizations handling appeals at volume, we have a custom tier — let’s talk.`}
        />

        <section className="bg-bg py-20">
          <div className="wrap">
            <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3">
              {tiers.map((tier) => (
                <TierCard key={tier.name} tier={tier} />
              ))}
            </div>
            <p className="mx-auto mt-12 max-w-2xl text-center text-xs text-muted">
              Outcome reporting (did your appeal succeed?) is requested via email after you export.
              Reporting is voluntary and unsubscribe is one click.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function TierCard({ tier }: { tier: Tier }) {
  const ctaClasses = `mt-7 w-full justify-center px-5 py-3 ${
    tier.highlight ? "btn-indigo" : "btn-glass"
  }`;

  return (
    <article
      className={`relative overflow-hidden flex flex-col rounded-xl border bg-surface p-7 transition ${
        tier.highlight
          ? "border-ink/40 pt-16 ring-1 ring-ink/10"
          : "border-border hover:border-primary/30"
      }`}
    >
      {tier.offer && <OfferBanner label={tier.offer} />}
      {tier.badge && (
        <span
          className={`mb-3 inline-flex w-max rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
            tier.highlight
              ? "bg-primary/10 text-primary"
              : "bg-highlight text-ink-muted"
          }`}
        >
          {tier.badge}
        </span>
      )}
      <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink">
        {tier.name}
      </h2>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-heading text-5xl font-extrabold leading-none tracking-tight text-ink">
          {tier.price}
        </span>
        {tier.cents && (
          <span className="font-heading text-2xl font-extrabold leading-none tracking-tight text-ink">
            {tier.cents}
          </span>
        )}
        {tier.originalPrice && (
          <span className="text-sm font-semibold text-muted line-through decoration-danger decoration-2">
            {tier.originalPrice}
          </span>
        )}
        {tier.priceSuffix && <span className="text-sm text-muted">{tier.priceSuffix}</span>}
      </div>
      <p className="mt-3 text-sm text-muted">{tier.blurb}</p>
      <ul className="mt-6 flex-1 space-y-2.5 text-sm text-ink">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check
              size={16}
              strokeWidth={2.25}
              aria-hidden
              className="mt-0.5 flex-shrink-0 text-success"
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {tier.external ? (
        <a href={tier.ctaTo} className={ctaClasses}>
          {tier.cta}
        </a>
      ) : (
        <AppEntryLink to={tier.ctaTo} className={ctaClasses}>
          {tier.cta}
        </AppEntryLink>
      )}
    </article>
  );
}

function OfferBanner({ label }: { label: string }) {
  const lines = label.split(" — ");

  return (
    <div className="absolute right-[-76px] top-9 z-10 flex w-72 rotate-45 items-center justify-center border-y border-white/25 bg-danger px-12 py-3 text-white shadow-[0_12px_28px_rgba(179,47,47,0.32)]">
      <div className="translate-x-1 text-center text-[11px] font-extrabold uppercase leading-tight tracking-[0.12em]">
        {lines.map((line) => (
          <span key={line} className="block whitespace-nowrap">
            {line}
          </span>
        ))}
      </div>
    </div>
  );
}
